import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/feature/SectionHeading";
import VideoCard from "@/components/feature/VideoCard";
import VideoCardSkeleton from "@/components/feature/VideoCardSkeleton";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryGrid from "@/pages/home/components/CategoryGrid";
import PopularSection from "@/pages/home/components/PopularSection";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getLatestVideos, getPopularVideos } from "@/utils/video";

export default function Home() {
  const [loading, setLoading] = useState(true);

  useDocumentMeta({
    title: "亚洲激情视频 - 精选亚洲激情类视频资源在线浏览",
    description:
      "亚洲激情视频网站提供丰富的亚洲激情主题视频内容，涵盖多种类型供用户浏览和选择，打造专业的视频内容聚合平台。",
    keywords: "亚洲激情视频,激情视频,亚洲视频,视频内容,亚洲影视,激情电影,视频资源",
  });

  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 420);
    return () => window.clearTimeout(timer);
  }, []);

  const heroItems = useMemo(() => getPopularVideos(4), []);
  const latestVideos = useMemo(() => getLatestVideos(8), []);
  const popularVideos = useMemo(() => getPopularVideos(6), []);

  return (
    <SiteLayout>
      <HeroCarousel items={heroItems} />

      <section className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
        <SectionHeading
          eyebrow="Browse by category"
          title="按主题浏览内容"
          description="六个内容方向，覆盖夜色、都市、剧情、写真、经典与幕后，按你当下的心情挑一个入口。"
        />
        <CategoryGrid />
      </section>

      <section className="border-y border-background-800 bg-background-900/40">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
          <SectionHeading
            eyebrow="Latest releases"
            title="精选最新视频"
            description="按发布时间排序，近期上线的精选片目都在这里。"
            actionText="进入分类频道"
            actionTo="/category/night"
          />

          {loading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, index) => (
                <VideoCardSkeleton key={index} />
              ))}
            </div>
          ) : latestVideos.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-700 px-6 py-16 text-center">
              <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-800">
                <i className="ri-film-line text-2xl text-primary-400" />
              </span>
              <h3 className="font-heading text-base font-semibold text-foreground-100">
                暂无最新视频
              </h3>
              <p className="mt-2 text-sm text-foreground-500">
                编辑部正在整理新的片目，稍后再来看看吧。
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {latestVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
        <SectionHeading
          eyebrow="Most watched"
          title="热门视频推荐"
          description="根据站内访问量整理的榜单，适合不知道从哪一部开始的时候点开。"
          actionText="进入搜索页"
          actionTo="/search"
        />
        <PopularSection items={popularVideos} />
      </section>

      <section className="border-t border-background-800 bg-background-900/60">
        <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 gap-10 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-primary-400">
              <span className="h-px w-6 bg-primary-600" />
              About this site
            </p>
            <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-50 md:text-3xl">
              一个认真做分类与资料的
              <br className="hidden md:block" />
              亚洲影像内容聚合站
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-foreground-400">
              亚洲激情视频不做杂乱堆叠的入口页。我们把每一部作品按主题、类型、时长与发布时间整理好，配上完整的简介与标签，让你在几分钟内找到想看的片段，而不是在无数链接里反复跳转。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-400">
              站内所有视频标题、封面与简介均为虚构示例，用于展示内容聚合与筛选浏览的完整产品形态。播放与分享为前端演示，不提供真实视频流服务。
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/about"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-500"
              >
                了解站点说明
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </span>
              </Link>
              <Link
                to="/about#contact"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-5 py-2.5 text-sm text-foreground-200 transition-colors hover:border-primary-600 hover:text-primary-200"
              >
                提交反馈
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {[
              {
                icon: "ri-layout-grid-line",
                title: "结构化分类",
                desc: "六大主题频道，每部作品都有明确的归类，不做模糊标签。",
              },
              {
                icon: "ri-filter-3-line",
                title: "多维筛选",
                desc: "按类型、时长、发布日期组合筛选，结果实时刷新。",
              },
              {
                icon: "ri-search-eye-line",
                title: "关键词检索",
                desc: "标题、标签、分类同时匹配，快速定位目标片目。",
              },
              {
                icon: "ri-shield-check-line",
                title: "内容规范",
                desc: "示例内容均为虚构，全站标注 18+ 提示与免责声明。",
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className="rounded-lg border border-background-800 bg-background-900 p-5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-950 text-primary-300">
                  <i className={`${feature.icon} text-lg`} />
                </span>
                <h3 className="mt-4 font-heading text-sm font-semibold text-foreground-100">
                  {feature.title}
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-500">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}